import React, { useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/Card";

// Hebrew day-of-week labels (Sun=0 … Sat=6)
const DAY_LABELS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const TIME_SLOTS = [
  { label: "22:00 - 24:00", min: 22, max: 23 },
  { label: "20:00 - 22:00", min: 20, max: 21 },
  { label: "18:00 - 20:00", min: 18, max: 19 },
  { label: "16:00 - 18:00", min: 16, max: 17 },
  { label: "14:00 - 16:00", min: 14, max: 15 },
  { label: "12:00 - 14:00", min: 12, max: 13 },
  { label: "10:00 - 12:00", min: 10, max: 11 },
  { label: "08:00 - 10:00", min: 8, max: 9 },
  { label: "06:00 - 08:00", min: 6, max: 7 },
  { label: "00:00 - 06:00", min: 0, max: 5 },
];

function getDistinctDayCounts(dataByDuration) {
  const dayDates = Array.from({ length: 7 }, () => new Set());
  (dataByDuration || []).forEach((gateItem) => {
    (gateItem?.result || []).forEach((record) => {
      if (record.date) {
        const d = new Date(record.date);
        const dateStr = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        const dow = d.getDay();
        dayDates[dow].add(dateStr);
      }
    });
  });
  return dayDates.map((set) => Math.max(1, set.size));
}

function aggregateByDayOfWeek(resultArray, dayCounts) {
  const dayTotals = Array.from({ length: 7 }, () => 0);
  (resultArray || []).forEach((record) => {
    if (record.date) {
      const dow = new Date(record.date).getDay();
      dayTotals[dow] += record.entering || 0;
    }
  });
  return dayTotals.map((tot, dow) => Math.round(tot / (dayCounts?.[dow] || 1)));
}

function aggregateByTimeSlotAndDay(dataByDuration, dayCounts) {
  const grid = Array.from({ length: TIME_SLOTS.length }, () =>
    Array.from({ length: 7 }, () => 0)
  );

  (dataByDuration || []).forEach((gateItem) => {
    (gateItem?.result || []).forEach((record) => {
      if (record.date) {
        const d = new Date(record.date);
        const dow = d.getDay();
        const hour = d.getHours();
        const slotIdx = TIME_SLOTS.findIndex(
          (slot) => hour >= slot.min && hour <= slot.max
        );
        if (slotIdx !== -1) {
          grid[slotIdx][dow] += record.entering || 0;
        }
      }
    });
  });

  return TIME_SLOTS.map((slot, sIdx) => ({
    name: slot.label,
    data: DAY_LABELS.map((day, d) => ({
      x: day,
      y: Math.round(grid[sIdx][d] / (dayCounts?.[d] || 1)),
    })),
  }));
}

export function AtarimHeatmapChart({ deviceNames = [], dataByDuration = [] }) {
  const [viewMode, setViewMode] = useState("location"); // "location" | "hourly"

  const dayCounts = useMemo(() => {
    return getDistinctDayCounts(dataByDuration);
  }, [dataByDuration]);

  const locationSeries = useMemo(() => {
    return dataByDuration.map((item, i) => {
      const totals = aggregateByDayOfWeek(item?.result || [], dayCounts);
      const label = deviceNames[i] || `כניסה ${i + 1}`;
      return {
        name: label,
        data: DAY_LABELS.map((day, d) => ({ x: day, y: totals[d] })),
      };
    });
  }, [dataByDuration, deviceNames, dayCounts]);

  const hourlySeries = useMemo(() => {
    return aggregateByTimeSlotAndDay(dataByDuration, dayCounts);
  }, [dataByDuration, dayCounts]);

  const series = viewMode === "location" ? locationSeries : hourlySeries;

  const options = useMemo(() => ({
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: { enabled: true },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: viewMode === "location" ? "11px" : "10px",
        fontWeight: "600",
        colors: ["#1f2937"],
      },
      formatter: (val) => (val > 0 ? val.toLocaleString() : ""),
    },
    // Same palette/logic as NationalParkHeatmapChart: single amber base + Apex shade intensity.
    colors: ["#f59e0b"],
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.75,
        radius: 4,
        enableShades: true,
      },
    },
    xaxis: {
      type: "category",
      labels: {
        style: { fontSize: "11px", fontWeight: "600", colors: "#374151" },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: viewMode === "location" ? "11px" : "10px",
          fontWeight: "500",
          colors: "#374151",
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) =>
          val > 0
            ? `${viewMode === "location" ? 'סה"כ כניסות' : 'סה"כ במתחם'}: ${val.toLocaleString()}`
            : "אין נתונים",
      },
    },
    legend: { show: false },
    stroke: { width: 2, colors: ["#fff"] },
  }), [viewMode]);

  return (
    <Card className="h-full flex flex-col justify-between">
      <CardHeader className="flex flex-row items-center justify-between border-b py-3 px-4 min-h-[73px]">
        {/* Toggle Pills (Left in RTL) */}
        <div className="flex items-center gap-1 bg-gray-100/90 p-1 rounded-lg border border-gray-200/70 text-xs">
          <button
            type="button"
            onClick={() => setViewMode("location")}
            className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${viewMode === "location"
                ? "bg-white text-blue-700 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            לפי מיקום
          </button>
          <button
            type="button"
            onClick={() => setViewMode("hourly")}
            className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${viewMode === "hourly"
                ? "bg-white text-blue-700 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            לפי שעה
          </button>
        </div>

        {/* Title and Description (Right in RTL) */}
        <div className="text-right">
          <CardTitle className="text-base font-bold text-gray-900">
            {viewMode === "location"
              ? "עומס שבועי לפי מיקום"
              : "עומס שבועי לפי שעה"}
          </CardTitle>
          <CardDescription className="text-xs text-gray-500">
            {viewMode === "location"
              ? "סה״כ כניסות לפי ימי השבוע ומיקום שער"
              : "סה״כ כניסות לכלל המתחם לפי שעות וימי השבוע"}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-2 sm:px-4 sm:pt-4 flex-1 flex flex-col justify-center">
        <div className="aspect-auto h-[303px] w-full">
          <ReactApexChart
            options={options}
            series={series}
            type="heatmap"
            height={303}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default AtarimHeatmapChart;
